/**
 * CategoryView - Shows SEO issues organized by category/section
 */

import { useState } from 'react'
import { ChevronRight, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import type { ParsedIssues } from './types'

interface CategoryViewProps {
  issues: ParsedIssues
}

export function CategoryView({ issues }: CategoryViewProps) {
  // Sort sections: those with issues first, then by name
  const sortedSections = Object.entries(issues.bySection).sort(([, a], [, b]) => {
    const aHasIssues = a.issues.length > 0
    const bHasIssues = b.issues.length > 0
    if (aHasIssues && !bHasIssues) return -1
    if (!aHasIssues && bHasIssues) return 1
    return a.name.localeCompare(b.name)
  })

  if (sortedSections.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-4 text-center">
        No category data available
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {sortedSections.map(([uid, section]) => (
        <CategorySection key={uid} uid={uid} section={section} />
      ))}
    </div>
  )
}

interface CategorySectionProps {
  uid: string
  section: {
    name: string
    issues: ParsedIssues['byStatus']['error']
    passedCount: number
  }
}

function CategorySection({ uid, section }: CategorySectionProps) {
  const [open, setOpen] = useState(false)

  const errorCount = section.issues.filter((i) => i.status === 'error').length
  const warningCount = section.issues.filter((i) => i.status === 'warning').length
  const noticeCount = section.issues.filter((i) => i.status === 'notice').length
  const hasIssues = section.issues.length > 0

  // Determine section status color
  const statusColor = errorCount > 0
    ? 'text-red-500'
    : warningCount > 0
      ? 'text-yellow-500'
      : noticeCount > 0
        ? 'text-blue-500'
        : 'text-green-500'

  const StatusIcon = errorCount > 0
    ? AlertCircle
    : warningCount > 0
      ? AlertTriangle
      : noticeCount > 0
        ? Info
        : CheckCircle2

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-2 rounded hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-2">
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              open ? 'rotate-90' : ''
            }`}
          />
          <StatusIcon className={`h-4 w-4 ${statusColor}`} />
          <span className="text-sm font-medium">{section.name}</span>
        </div>
        <div className="flex items-center gap-2">
          {hasIssues ? (
            <span className="text-xs text-muted-foreground">
              {section.issues.length} issue{section.issues.length !== 1 ? 's' : ''}
            </span>
          ) : (
            <span className="text-xs text-green-600 dark:text-green-400">
              all passed
            </span>
          )}
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="ml-6 pl-4 border-l border-border space-y-1 py-2">
          {section.issues.length > 0 ? (
            section.issues.map((issue) => (
              <div
                key={`${uid}-${issue.code}`}
                className="flex items-center justify-between py-1 text-sm"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <IssueStatusDot status={issue.status} />
                  <span className="truncate" title={issue.name}>
                    {issue.name}
                  </span>
                </div>
                {issue.value > 0 && (
                  <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                    {issue.value} {issue.value === 1 ? 'page' : 'pages'}
                  </span>
                )}
              </div>
            ))
          ) : (
            <div className="text-sm text-muted-foreground py-1">
              {section.passedCount} check{section.passedCount !== 1 ? 's' : ''} passed
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function IssueStatusDot({ status }: { status: 'error' | 'warning' | 'notice' | 'passed' }) {
  const colorClass =
    status === 'error'
      ? 'bg-red-500'
      : status === 'warning'
        ? 'bg-yellow-500'
        : status === 'notice'
          ? 'bg-blue-500'
          : 'bg-green-500'

  return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colorClass}`} />
}
